import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/cn.js';

/** Frosted glass card — the default surface. */
export function Glass({ className, hover, as: Tag = 'section', children, ...rest }) {
  return <Tag className={cn('glass p-6', hover && 'glass-hover', className)} {...rest}>{children}</Tag>;
}

export function Button({ variant = 'primary', size = 'md', className, children, ...rest }) {
  const sizes = { sm: 'text-[0.8rem] px-4 py-2', md: '', lg: 'text-base px-7 py-4' };
  return (
    <button className={cn('btn', `btn-${variant}`, sizes[size], className)} {...rest}>
      {children}
    </button>
  );
}

export function Field({ label, hint, className, ...rest }) {
  return (
    <label className="block space-y-2">
      {label && <span className="eyebrow block">{label}</span>}
      <input className={cn('input', className)} {...rest} />
      {hint && <span className="block text-xs" style={{ color: 'var(--text-3)' }}>{hint}</span>}
    </label>
  );
}

const TONES = {
  neutral:  { color: 'var(--text-2)',  bg: 'var(--glass-2)' },
  active:   { color: 'var(--sage)',    bg: 'color-mix(in oklab, var(--sage) 14%, transparent)' },
  building: { color: 'var(--gold)',    bg: 'color-mix(in oklab, var(--gold) 14%, transparent)' },
  expired:  { color: 'var(--text-3)',  bg: 'var(--glass)' },
  warn:     { color: 'var(--apricot)',    bg: 'color-mix(in oklab, var(--apricot) 14%, transparent)' },
  static:   { color: 'var(--rust)',  bg: 'color-mix(in oklab, var(--rust) 14%, transparent)' },
  violet:   { color: 'var(--sage)',  bg: 'color-mix(in oklab, var(--sage) 14%, transparent)' },
};
export function Tag({ tone = 'neutral', dot, children, className }) {
  const t = TONES[tone] ?? TONES.neutral;
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[0.68rem] font-semibold tracking-[0.08em] uppercase',
                        'px-2.5 py-1 rounded-full', className)}
          style={{ color: t.color, background: t.bg }}>
      {dot && <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'currentColor' }} />}
      {children}
    </span>
  );
}

export function Heading({ eyebrow, title, action, className }) {
  return (
    <div className={cn('flex items-end justify-between gap-4 mb-5', className)}>
      <div>
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h2 className="display text-[1.75rem] md:text-[2rem]">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function Empty({ children, className }) {
  return <p className={cn('text-sm leading-relaxed', className)} style={{ color: 'var(--text-3)' }}>{children}</p>;
}

/** Stat block: label above a large number. */
export function Stat({ label, value, accent, className }) {
  return (
    <div className={cn('glass-flat p-4', className)}>
      <p className="eyebrow">{label}</p>
      <p className="display text-2xl mt-1.5 nums" style={{ color: accent ? `var(--${accent})` : 'var(--text)' }}>{value}</p>
    </div>
  );
}

/** Bottom sheet on phones, centred dialog on wide screens. */
/**
 * Rendered into document.body: pages sit inside <main class="relative z-[1]">,
 * which is its own stacking context, so a fixed sheet left in place would slide
 * under the z-40 tab bar no matter how high its own z-index.
 */
export function Sheet({ children, onClose, wide }) {
  return createPortal(
    <AnimatePresence>
      <motion.div key="scrim"
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6"
        style={{ background: 'color-mix(in oklab, var(--ground) 70%, transparent)', backdropFilter: 'blur(10px)' }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}>
        <motion.div
          className={cn('glass w-full overflow-y-auto p-5 sm:p-6 space-y-4 !rounded-b-none sm:!rounded-b-[var(--radius-2xl)]',
                        wide ? 'sm:max-w-lg' : 'sm:max-w-md')}
          style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))',
                   maxHeight: 'calc(100dvh - env(safe-area-inset-top) - 1rem)', overscrollBehavior: 'contain' }}
          initial={{ y: 40, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 20, opacity: 0 }} transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          onClick={(e) => e.stopPropagation()}>
          <div className="sm:hidden mx-auto h-1 w-10 rounded-full -mt-2" style={{ background: 'var(--edge-lit)' }} />
          {children}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}

/** Soft notice row with an action. */
export function Notice({ children, to, label, tone = 'violet', Link }) {
  const t = TONES[tone] ?? TONES.violet;
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl"
         style={{ background: t.bg, border: `1px solid color-mix(in oklab, ${t.color} 25%, transparent)` }}>
      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: t.color, boxShadow: `0 0 12px ${t.color}` }} />
      <p className="text-sm flex-1" style={{ color: 'var(--text-2)' }}>{children}</p>
      {to && Link && (
        <Link to={to} className="text-xs font-semibold whitespace-nowrap hover:opacity-100 opacity-80"
              style={{ color: t.color }}>{label}</Link>
      )}
    </div>
  );
}
